import {
  Box,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
} from '@chakra-ui/react';
import { useRef, useState } from 'react';
import { debounce } from 'lodash-es';
import { SearchMd } from '@kula-ai/icons/dual-tone/SearchMd';
import { RecentSearchView } from './RecentSearchView';
import { initialSearchResults, SearchManagerProvider } from './search-manager';
import { SearchResults } from './types';
import { useSearchHelper } from './useSearchHelper';
import { SearchLoading } from './SearchLoading';
import { SearchResultsTabs } from './SearchResultsTabs';
import { useSearchTabs } from './useSearchTabs';
import { XClose } from '@kula-ai/icons/solid/XClose';
import { RESUME_SEARCH_STORAGE_KEY } from './constant';

export const GlobalSearch = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const searchInput = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = useState<number>(0);
  const [searchResults, setSearchResults] =
    useState<SearchResults>(initialSearchResults);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const { currentTab, setCurrentTab, permittedTabIds } =
    useSearchTabs(searchResults);
  const { handleSearch } = useSearchHelper();
  const modalContainerRef = useRef<HTMLDivElement>(null);
  const [includeResume, setIncludeResume] = useState(
    localStorage?.getItem(RESUME_SEARCH_STORAGE_KEY) === 'true'
  );

  const setSearchText = (text: string) => {
    if (searchInput.current) {
      searchInput.current.value = text;
      setSearchTerm(searchInput?.current?.value);
      handleSearch({
        searchTerm: text,
        setIsSearchLoading,
        setSearchResults,
        permittedTabIds,
        includeResume,
      });
    }
  };
  const handleChange = debounce(() => {
    if (searchInput?.current?.value?.trim() === '') {
      setSearchTerm('');
      setSearchResults(initialSearchResults);
      return;
    }
    setSearchTerm(searchInput?.current?.value);
    handleSearch({
      searchTerm: searchInput?.current?.value,
      setIsSearchLoading,
      setSearchResults,
      permittedTabIds,
      includeResume,
    });
  }, 300);

  const handleKeyboardNavigation = ({
    direction,
  }: {
    direction: 'up' | 'down';
  }) => {
    const items = document.querySelectorAll('.universal--search--item');
    const hasSelectedItem = Array?.from(items)?.some((item) =>
      item?.classList?.contains('active')
    );

    // If there is no selected item, add the active class to the first item and set the selected index to 0
    if (!hasSelectedItem) {
      items?.[0]?.classList?.add('active');
      setSelected(0);
      return;
    }

    let index = 0;
    let newIndex = 0;
    let newItem: Element;

    if (direction === 'down') {
      items?.forEach((_, i) => {
        if (i === selected) {
          index = i;
        }
      });

      newIndex = index === items?.length - 1 ? 0 : index + 1;
    } else if (direction === 'up') {
      items?.forEach((_, i) => {
        if (i === selected) {
          index = i;
        }
      });

      newIndex = !index ? items?.length - 1 : index - 1;
    } else {
      setSelected(0);
    }

    newItem = items[newIndex];

    if (newItem && typeof newIndex === 'number') {
      items[selected]?.classList?.remove('active');
      setSelected(newIndex);
      newItem?.classList?.add('active');
      newItem?.scrollIntoView({
        behavior: 'smooth',
        block: newIndex ? 'center' : 'end',
      });
    }
  };

  const handleKeyboardEnter = () => {
    const items = document.querySelectorAll('.universal--search--item');
    const hasSelectedItem = Array.from(items).some((item) =>
      item.classList.contains('active')
    );

    if (!hasSelectedItem) return;

    let index = 0;
    let item: Element | null;

    items?.forEach((_, i) => {
      if (i === selected) {
        index = i;
      }
    });

    item = items[index];

    if (item) {
      (item as HTMLElement).click();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const keyHandlers = {
      ArrowDown: 'down',
      ArrowUp: 'up',
      Enter: null,
    };

    const direction = keyHandlers[e.key];
    if (direction !== undefined) {
      e.preventDefault();
      e.stopPropagation();
      if (direction) {
        handleKeyboardNavigation({ direction });
      }
      if (e.key === 'Enter') handleKeyboardEnter();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <SearchManagerProvider
        value={{
          searchTerm,
          setSearchTerm,
          isSearchLoading,
          setIsSearchLoading,
          onSearchClose: onClose,
          searchResults,
          setSearchResults,
          selected,
          setSelected,
          modalContainerRef,
          setSearchText,
          handleKeyboardEnter,
          handleKeyboardNavigation,
          currentTab,
          setCurrentTab,
          handleKeyDown,
          includeResume,
          setIncludeResume,
        }}
      >
        <ModalContent
          borderRadius="xs"
          minWidth="62rem"
          border="1px solid"
          borderColor="secondary-100"
          pos="fixed"
          mt="6px"
          // boxShadow="2px 4px 10px 0px #D7DAEB1A,8px 17px 19px 0px #D7DAEB17,18px 38px 25px 0px #D7DAEB0D,32px 68px 30px 0px #D7DAEB03,50px 107px 33px 0px #D7DAEB00"
        >
          <ModalBody p="0" ref={modalContainerRef}>
            <InputGroup
              height="62px"
              alignItems="center"
              onKeyDown={(e) => {
                handleKeyDown(e);
              }}
            >
              <InputLeftElement
                height="100%"
                pointerEvents="none"
                pl="14px"
                width="unset"
                children={
                  <Icon
                    as={SearchMd}
                    color="secondary-300"
                    width="1.5em"
                    height="1.5em"
                  />
                }
              />
              <Input
                pl="48px"
                height="100%"
                ref={searchInput}
                onChange={handleChange}
                border="none"
                _focus={{ border: 'none', boxShadow: 'none' }}
                _hover={{ border: 'none' }}
                _active={{ border: 'none' }}
                _focusVisible={{ boxShadow: 'none' }}
                fontSize="sm"
                fontWeight="regular"
                // cmt this line due to the selection range issue
                // onClick={() => {
                //   if (searchInput?.current?.selectionStart === 0) {
                //     const length = searchInput?.current?.value?.length;
                //     searchInput?.current?.setSelectionRange(length, length);
                //   }
                // }}
              />
              {searchTerm?.length > 1 && (
                <InputRightElement
                  height="100%"
                  width="unset"
                  pr="14px"
                  children={
                    <Icon
                      as={XClose}
                      color="secondary-700"
                      width="1.5em"
                      height="1.5em"
                      cursor="pointer"
                      onClick={() => {
                        if (searchInput.current) {
                          searchInput.current.value = '';
                          searchInput.current.focus();
                        }
                        setSearchTerm('');
                        setSearchResults(initialSearchResults);
                      }}
                    />
                  }
                />
              )}
            </InputGroup>
            <Box borderTop="1px solid" borderColor="secondary-100">
              {isSearchLoading ? (
                <SearchLoading />
              ) : searchTerm?.length >= 2 ? (
                <SearchResultsTabs />
              ) : (
                <RecentSearchView />
              )}
            </Box>
          </ModalBody>
        </ModalContent>
      </SearchManagerProvider>
    </Modal>
  );
};
